/**
 * The direct manager deletes a team member's pending leave request (2026-11).
 *
 * A written reason of at least 10 characters is required. The server keeps the
 * record and the reason, returns the reserved days, and the employee sees
 * "Deleted by manager" with the reason in My Leave.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const DELETABLE = ['pending_manager', 'pending_hr'];

export const canManagerDelete = (request) => DELETABLE.includes(request.status) && !request.is_archived;

export const ManagerDeleteLeaveButton = ({ request, isRTL, onDeleted }) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!canManagerDelete(request)) return null;

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/leave/requests/${request.id}/manager-delete`,
        { reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      setError(e.response?.data?.detail?.error || (isRTL ? 'تعذّر حذف الطلب، حاول مرة أخرى' : 'Delete failed; please try again'));
    } finally {
      setBusy(false);
    }
  };

  return <>
    <Button
      size="sm"
      variant="ghost"
      className="text-red-700 hover:text-red-800 hover:bg-red-50"
      onClick={() => { setOpen(true); setReason(''); setError(''); }}
      data-testid={`manager-delete-${request.id}`}
    >
      <Trash2 className="h-4 w-4 me-1" />{isRTL ? 'حذف' : 'Delete'}
    </Button>
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent className="max-w-lg" data-testid={`manager-delete-dialog-${request.id}`}>
        <DialogHeader>
          <DialogTitle>{isRTL ? 'حذف طلب الإجازة' : 'Delete leave request'}</DialogTitle>
          <DialogDescription>
            {isRTL
              ? `${request.employee_name?.trim() || ''} — سيظهر للموظف أن المدير حذف الطلب مع السبب، وتعود الأيام المحجوزة إلى رصيده.`
              : `${request.employee_name?.trim() || ''} — the employee will see that you deleted it, with your reason, and the reserved days go back to their balance.`}
          </DialogDescription>
        </DialogHeader>
        <Label htmlFor={`manager-delete-reason-${request.id}`}>
          {isRTL ? 'سبب الحذف (10 أحرف على الأقل)' : 'Reason for deleting (at least 10 characters)'} *
        </Label>
        <Textarea
          id={`manager-delete-reason-${request.id}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={1000}
          rows={3}
          data-testid={`manager-delete-reason-${request.id}`}
        />
        {error && <p role="alert" className="text-sm text-red-700" data-testid={`manager-delete-error-${request.id}`}>{error}</p>}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
          <Button
            variant="destructive"
            disabled={busy || reason.trim().length < 10}
            onClick={submit}
            data-testid={`manager-delete-confirm-${request.id}`}
          >
            {busy ? (isRTL ? 'جارٍ الحذف…' : 'Deleting…') : (isRTL ? 'حذف' : 'Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
};

export default ManagerDeleteLeaveButton;
